import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useViewMode } from '@/shared/hooks/useViewMode';
import { Chip, EmptyState } from '@/shared/ui/atoms';
import { Markdown } from '@/shared/ui/Markdown';
import { ViewModeToggle, type ViewModeOption } from '@/shared/ui/ViewModeToggle';
import { DevRequestSummaryRow } from '@/features/project-board/components/DevRequestSummaryRow';
import { devRequestApi } from '@/features/project-board/api';
import { SortPicker } from '@/features/project-board/components/SortPicker';
import { DateSort, type SortKey } from '@/features/project-board/sorting';
import { DetailDrawer, type DrawerPayload } from '@/features/project-board/components/DetailDrawer';
import { DrawerPayloads } from '@/features/project-board/drawer-payloads';
import { DevRequestFlow, type DevRequestLaneGroup } from '@/features/project-board/dev-request-flow';
import { OwnerRoster } from '@/features/project-board/owners';
import type {
  DevRequest,
  DevRequestAttachment,
  DevRequestRevision,
  DevRequestSpecLink,
} from '@/features/project-board/types';

/**
 * 개발요청 인박스 — 다른 팀이 슬랙 봇에게 보낸 요청을 보고, 그게 어떤 스펙이 돼 어디까지 갔는지 본다.
 *
 * 보드 화면들과 달리 `BoardProvider`를 쓰지 않는다. 원천이 색인이 아니라 인입이라 필터·저장소 탭이
 * 걸리지 않고, 같은 인덱스를 나눠 보지도 않는다. 연결된 스펙만 색인에서 와서 목록 응답에 함께 실린다.
 *
 * 상세는 다른 보드와 같은 `DetailDrawer`로 연다. 단 `ViewFrame`은 거치지 않는다 — 그건 `BoardProvider`의
 * 필터 바·저장소 탭을 함께 그리는 껍데기라 이 화면에는 걸리는 게 없다. 여는 방식만 맞추고 껍데기는 안 쓴다.
 *
 * 연 카드는 `ViewFrame`과 같은 규약(`kind`/`item` 쿼리)으로 주소창에도 반영한다 — 그 주소를 복사해
 * 붙여넣으면 같은 카드가 열린 채로 뜬다. `DrawerPayloads.resolveLinked`(다른 화면이 쓰는 공용 복원 함수)는 쓰지
 * 않는다 — 그건 `BoardProvider`의 색인에서 항목을 찾고, 개발요청은 그 색인이 아니라 이 화면 자신의
 * `requests` 목록에서 온다.
 */
type DevRequestViewMode = 'board' | 'list';

const SORT_OPTIONS = DateSort.options('접수일');

const VIEW_MODES: ReadonlyArray<ViewModeOption<DevRequestViewMode>> = [
  { key: 'board', label: '보드', glyph: '▤' },
  { key: 'list', label: '목록', glyph: '☰' },
];

export function DevRequestView(): React.JSX.Element {
  const [requests, setRequests] = useState<DevRequest[] | null>(null);
  const [specs, setSpecs] = useState<DevRequestSpecLink[]>([]);
  const [platforms, setPlatforms] = useState<string[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [mode, setMode] = useViewMode<DevRequestViewMode>('project-board.dev-request.view', 'board');
  const [opened, setOpened] = useState<number | null>(null);
  // 기본 정렬은 최근 접수일 순.
  const [sort, setSort] = useViewMode<SortKey>('project-board.dev-request.sort', 'date-desc');
  const [assignee, setAssignee] = useState('');
  const [searchParams, setSearchParams] = useSearchParams();
  const restoredFromLink = useRef(false);

  const load = useCallback(() => {
    devRequestApi
      .list()
      .then((payload) => {
        setRequests(payload.requests);
        setSpecs(payload.specs);
        setPlatforms(payload.platforms ?? []);
        setError(null);
      })
      .catch((cause: Error) => setError(cause.message));
  }, []);

  useEffect(load, [load]);

  // 공유 링크(`?kind=dev-request&item=#42`)로 들어왔으면 목록이 실린 뒤 그 카드를 한 번 연다.
  // 그 뒤로는 사용자가 누른 카드가 정본이라 다시 읽지 않는다 — `requests`가 갱신될 때마다 되돌리면
  // 사용자가 이미 닫은 카드가 다시 열린다.
  useEffect(() => {
    if (restoredFromLink.current || !requests) return;
    restoredFromLink.current = true;
    if (searchParams.get('kind') !== 'dev-request') return;
    const id = idOfItemParam(searchParams.get('item'));
    if (id !== null && requests.some((request) => request.id === id)) setOpened(id);
  }, [requests, searchParams]);

  const open = useCallback(
    (id: number) => {
      setOpened(id);
      setSearchParams(
        (current) => {
          const next = new URLSearchParams(current);
          next.set('kind', 'dev-request');
          next.set('item', `#${id}`);
          return next;
        },
        { replace: true },
      );
    },
    [setSearchParams],
  );

  const close = useCallback(() => {
    setOpened(null);
    setSearchParams(
      (current) => {
        const next = new URLSearchParams(current);
        next.delete('kind');
        next.delete('item');
        return next;
      },
      { replace: true },
    );
  }, [setSearchParams]);

  const specsOf = useCallback(
    (request: DevRequest) => specs.filter((spec) => spec.devRequestId === request.id),
    [specs],
  );
  const openedRequest = requests?.find((request) => request.id === opened) ?? null;
  // 드로어는 payload의 **객체 동일성**으로 "다른 항목이 열렸다"를 판정한다(내부에서 드릴다운 스택을 다시
  // 세운다). 렌더마다 새로 만들면 그 이펙트가 매번 돌아 상태 갱신 → 재렌더가 서로를 부른다.
  const payload = useMemo(
    () => (openedRequest ? detailPayload(openedRequest, specs, platforms, load) : null),
    [openedRequest, specs, platforms, load],
  );

  if (error) return <EmptyState message={`불러오지 못했다 — ${error}`} />;
  if (!requests) return <EmptyState message="개발요청을 불러오는 중…" />;
  if (requests.length === 0) {
    return <EmptyState message="아직 들어온 개발요청이 없다. 슬랙에서 봇을 멘션해 요청을 보내면 여기 쌓인다." />;
  }

  const filtered = assignee ? requests.filter((request) => request.assignee === assignee) : requests;
  // 개발요청의 등록 시점은 최초 접수 시각이다(`requestedAt`은 개정마다 바뀐다). 같은 시각은 id로 고정한다.
  const rows = DateSort.sort(filtered, sort, (request) => request.receivedAt, (request) => String(request.id));

  return (
    <div className="flex flex-col gap-3">
      <div className="flex flex-wrap items-center gap-3">
        <div className="text-[11.5px] text-muted">{rows.length}건</div>

        <select
          className="rounded-md border border-line bg-surface px-2.5 py-1.5 max-w-[160px]"
          value={assignee}
          onChange={(event) => setAssignee(event.target.value)}
        >
          <option value="">모든 담당자</option>
          {OwnerRoster.MEMBER_NAMES.map((name) => (
            <option key={name} value={name}>
              {name}
            </option>
          ))}
        </select>

        <span className="flex-1" />
        <SortPicker options={SORT_OPTIONS} value={sort} onChange={setSort} label="개발요청 정렬" />
        <ViewModeToggle options={VIEW_MODES} value={mode} onChange={setMode} />
      </div>

      {rows.length === 0 ? (
        <p className="rounded-md border border-line-soft bg-canvas px-3 py-2 text-[11.5px] text-muted">
          조건에 맞는 개발요청이 없다.
        </p>
      ) : mode === 'board' ? (
        <LaneBoard
          groups={DevRequestFlow.groupIntoLanes(rows, specs)}
          platforms={platforms}
          openedId={opened}
          onOpen={open}
          onAssigned={load}
        />
      ) : (
        <div className="flex flex-col gap-2">
          {rows.map((request) => (
            <RequestRow
              key={request.id}
              request={request}
              specs={specsOf(request)}
              platforms={platforms}
              selected={opened === request.id}
              onOpen={open}
              onAssigned={load}
            />
          ))}
        </div>
      )}

      <DetailDrawer payload={payload} onClose={close} />
    </div>
  );
}

/**
 * 드로어에 실을 것.
 *
 * 원문은 이미 손에 있어 `body`로 바로 간다. 담당자·첨부·스펙 링크·개정 이력은 이 화면만 아는 것이라
 * 여기서 그려 넘긴다 — 드로어가 항목 종류마다 분기를 갖지 않게 하려는 것이다. 개정 이력은 드로어를 열
 * 때 비로소 받는다(`RevisionHistory`가 마운트되며 조회): 대부분의 요청은 개정이 없고 본문이 크다.
 *
 * `onAssigned`는 담당자 지정이 저장된 뒤 목록을 다시 불러오는 콜백이다 — 서버가 정한 값(하네스
 * 기록·스레드 알림까지 끝난 뒤의 값)을 그대로 반영하려는 것이지, 화면에서 낙관적으로 미리 그리지
 * 않는다.
 */
function detailPayload(
  request: DevRequest,
  allSpecs: DevRequestSpecLink[],
  platforms: string[],
  onChanged: () => void,
): DrawerPayload {
  const specs = allSpecs.filter((spec) => spec.devRequestId === request.id);

  return DrawerPayloads.devRequest(request, specs, {
    header: (
      <>
        <AssigneePicker request={request} onAssigned={onChanged} />
        <PlatformPicker request={request} platforms={platforms} locked={specs.length > 0} onChanged={onChanged} />
        <BodyEditor request={request} onSaved={onChanged} />
        <AttachmentLinks attachments={request.attachments} />
        <LinkedSpecs specs={specs} slug={request.slug} />
        <div className="mb-3">
          <SlackLink href={request.permalink} />
        </div>
      </>
    ),
    footer:
      request.revision > 1 ? <RevisionHistory requestId={request.id} current={request.revision} /> : undefined,
  });
}

/**
 * 본문 수정 — 콘솔이 정본이라 여기서 고친 것이 하네스 문서·노션 카드까지 따라간다.
 *
 * 펼치기 전에는 단추 하나만 둔다. 개발요청 본문은 PRD를 통째로 싣는 일이 많아 늘 펼쳐 두면 드로어가
 * 편집기로 가려진다.
 *
 * 저장 뒤에는 `onSaved`로 목록을 다시 불러온다 — 화면에서 낙관적으로 미리 그리지 않는다. 서버가
 * 개정 번호를 올리고 하네스·노션까지 맞춘 **뒤의** 값이 정본이다(`AssigneePicker`와 같은 원칙).
 */
function BodyEditor({ request, onSaved }: { request: DevRequest; onSaved: () => void }): React.JSX.Element {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(request.body);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // 다른 카드를 열거나 서버가 새 본문을 준 뒤에는 초안을 그 값으로 되돌린다 — 안 그러면 이전에 열어
  // 둔 카드의 초안이 남아 엉뚱한 요청의 본문으로 저장된다.
  useEffect(() => {
    setDraft(request.body);
    setEditing(false);
    setError(null);
  }, [request.id, request.body]);

  const save = async (): Promise<void> => {
    setSaving(true);
    setError(null);
    try {
      await devRequestApi.editBody(request.id, draft);
      setEditing(false);
      onSaved();
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : String(cause));
    } finally {
      setSaving(false);
    }
  };

  if (!editing) {
    return (
      <div className="mb-3">
        <button
          type="button"
          onClick={() => setEditing(true)}
          className="rounded-md border border-line bg-surface px-2 py-1 text-[12px]"
        >
          본문 수정
        </button>
      </div>
    );
  }

  return (
    <div className="mb-3">
      <textarea
        value={draft}
        onChange={(event) => setDraft(event.target.value)}
        disabled={saving}
        rows={18}
        spellCheck={false}
        className="w-full rounded-md border border-line bg-surface px-2 py-1 font-mono text-[12px] disabled:opacity-60"
      />
      <div className="mt-2 flex flex-wrap items-center gap-2">
        <button
          type="button"
          onClick={() => void save()}
          disabled={saving || draft.trim().length === 0 || draft === request.body}
          className="rounded-md border border-line bg-surface px-2 py-1 text-[12px] disabled:opacity-60"
        >
          {saving ? '저장 중…' : '저장'}
        </button>
        <button
          type="button"
          onClick={() => {
            setDraft(request.body);
            setEditing(false);
            setError(null);
          }}
          disabled={saving}
          className="rounded-md border border-line bg-surface px-2 py-1 text-[12px] disabled:opacity-60"
        >
          취소
        </button>
        <span className="text-[11.5px] text-muted">저장하면 개정 번호가 올라가고 하네스 문서·노션 카드도 함께 갱신된다.</span>
      </div>
      {error && <div className="mt-1 text-[11px] text-rose-700">저장 실패 — {error}</div>}
    </div>
  );
}

/** 담당자 지정 — 명부(`owners.ts`)에서 고르거나 해제한다. 저장은 즉시(선택하는 순간) 반영된다. */
function AssigneePicker({ request, onAssigned }: { request: DevRequest; onAssigned: () => void }): React.JSX.Element {
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const change = async (event: React.ChangeEvent<HTMLSelectElement>): Promise<void> => {
    const assignee = event.target.value === '' ? null : event.target.value;
    setSaving(true);
    setError(null);
    try {
      await devRequestApi.assign(request.id, assignee);
      onAssigned();
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : String(cause));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="mb-3 flex flex-wrap items-center gap-2">
      <span className="text-[11.5px] text-muted">담당자</span>
      <select
        value={request.assignee ?? ''}
        onChange={(event) => void change(event)}
        disabled={saving}
        className="rounded-md border border-line bg-surface px-2 py-1 text-[12px] disabled:opacity-60"
      >
        <option value="">미지정</option>
        {OwnerRoster.MEMBER_NAMES.map((name) => (
          <option key={name} value={name}>
            {name}
          </option>
        ))}
      </select>
      {error && <span className="text-[11px] text-rose-700">저장 실패 — {error}</span>}
    </div>
  );
}

/**
 * 카드(보드 칸·목록 줄)에서 바로 담당자를 고르는 압축판. 저장 로직은 `AssigneePicker`와 같지만 라벨·
 * 에러 문구를 둘 자리가 없다 — 실패하면 `onAssigned`를 안 불러 다음 렌더에서 원래 값으로 되돌아간다.
 *
 * `onClick`에서 전파를 끊는다 — 카드 자신도 `onClick`으로 상세를 여는데, 그 안에 있는 select를 누르면
 * 카드 클릭까지 함께 발생해 드로어가 열려버린다(`RequestRow`의 슬랙 링크와 같은 이유).
 */
function AssigneeQuickPick({ request, onAssigned }: { request: DevRequest; onAssigned: () => void }): React.JSX.Element {
  const [saving, setSaving] = useState(false);

  const change = async (event: React.ChangeEvent<HTMLSelectElement>): Promise<void> => {
    const assignee = event.target.value === '' ? null : event.target.value;
    setSaving(true);
    try {
      await devRequestApi.assign(request.id, assignee);
      onAssigned();
    } catch (cause) {
      console.error(`담당자 지정 실패 — 개발요청 #${request.id}:`, cause);
    } finally {
      setSaving(false);
    }
  };

  return (
    <select
      value={request.assignee ?? ''}
      onChange={(event) => void change(event)}
      onClick={(event) => event.stopPropagation()}
      disabled={saving}
      title="담당자"
      className="rounded border border-line-soft bg-surface px-1 py-0.5 text-[11px] disabled:opacity-60"
    >
      <option value="">미지정</option>
      {OwnerRoster.MEMBER_NAMES.map((name) => (
        <option key={name} value={name}>
          {name}
        </option>
      ))}
    </select>
  );
}

/**
 * 플랫폼 변경 — 요청 문서를 적을 하네스 저장소를 고른다. 선택하는 순간 저장되고, 서버가 문서를 새 저장소로 옮긴 뒤의
 * 값이 정본이라 낙관적으로 미리 그리지 않는다(`AssigneePicker`와 같은 원칙).
 *
 * 연결된 스펙이 있으면 바꿀 수 없다(`locked`) — 스펙은 그 요청의 플랫폼 하네스에 있는 문서를 가리키므로 옮기면
 * 연결이 끊긴다. 서버는 연결 여부를 보지 않아 화면이 막는다.
 */
function PlatformPicker({
  request,
  platforms,
  locked,
  onChanged,
}: {
  request: DevRequest;
  platforms: string[];
  locked: boolean;
  onChanged: () => void;
}): React.JSX.Element {
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const change = async (event: React.ChangeEvent<HTMLSelectElement>): Promise<void> => {
    setSaving(true);
    setError(null);
    try {
      await devRequestApi.changePlatform(request.id, event.target.value);
      onChanged();
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : String(cause));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="mb-3 flex flex-wrap items-center gap-2">
      <span className="text-[11.5px] text-muted">플랫폼</span>
      <select
        value={request.platform}
        onChange={(event) => void change(event)}
        disabled={saving || locked}
        className="rounded-md border border-line bg-surface px-2 py-1 text-[12px] disabled:opacity-60"
      >
        {PlatformOptions.of(platforms, request.platform).map((platform) => (
          <option key={platform} value={platform}>
            {platform}
          </option>
        ))}
      </select>
      {locked && <span className="text-[11px] text-muted">연결된 스펙이 있어 바꿀 수 없다</span>}
      {error && <span className="text-[11px] text-rose-700">저장 실패 — {error}</span>}
    </div>
  );
}

/**
 * 카드(보드 칸·목록 줄)에서 바로 플랫폼을 고르는 압축판. 저장 로직은 `PlatformPicker`와 같지만 라벨·에러 문구를 둘 자리가
 * 없다 — 실패하면 `onChanged`를 안 불러 다음 렌더에서 원래 값으로 되돌아간다. `AssigneeQuickPick`처럼 클릭 전파를 끊는다.
 */
function PlatformQuickPick({
  request,
  platforms,
  locked,
  onChanged,
}: {
  request: DevRequest;
  platforms: string[];
  locked: boolean;
  onChanged: () => void;
}): React.JSX.Element {
  const [saving, setSaving] = useState(false);

  const change = async (event: React.ChangeEvent<HTMLSelectElement>): Promise<void> => {
    setSaving(true);
    try {
      await devRequestApi.changePlatform(request.id, event.target.value);
      onChanged();
    } catch (cause) {
      console.error(`플랫폼 변경 실패 — 개발요청 #${request.id}:`, cause);
    } finally {
      setSaving(false);
    }
  };

  return (
    <select
      value={request.platform}
      onChange={(event) => void change(event)}
      onClick={(event) => event.stopPropagation()}
      disabled={saving || locked}
      title={locked ? '연결된 스펙이 있어 플랫폼을 바꿀 수 없다' : '요청 문서가 적힌 하네스 저장소'}
      className="rounded border border-line-soft bg-surface px-1 py-0.5 text-[11px] disabled:opacity-60"
    >
      {PlatformOptions.of(platforms, request.platform).map((platform) => (
        <option key={platform} value={platform}>
          {platform}
        </option>
      ))}
    </select>
  );
}

/**
 * 플랫폼 선택지 — 서버가 준 목록이다. 요청의 현재 값이 목록에 없으면(설정이 바뀐 뒤 남은 요청) 그 값도 보여야 select가
 * 엉뚱한 첫 항목을 가리키지 않는다.
 */
class PlatformOptions {
  static of(platforms: string[], current: string): string[] {
    return platforms.includes(current) ? platforms : [current, ...platforms];
  }
}

/** 요청을 파이프라인 컬럼에 세운 보드. 컬럼 판정·묶기는 `dev-request-flow`가 하고 여기는 그리기만 한다. */
function LaneBoard({
  groups,
  platforms,
  openedId,
  onOpen,
  onAssigned,
}: {
  groups: DevRequestLaneGroup[];
  platforms: string[];
  openedId: number | null;
  onOpen: (id: number) => void;
  onAssigned: () => void;
}): React.JSX.Element {
  return (
    <div className="grid grid-flow-col auto-cols-[minmax(260px,1fr)] items-start gap-3 overflow-x-auto pb-2">
      {groups.map(({ lane, items }) => (
        <div key={lane.key} className="min-w-[260px] rounded-lg border border-line-soft bg-[#f0f3f6]">
          <div
            title={lane.hint}
            className="flex items-center gap-1.5 border-b border-line-soft px-3 py-2.5 text-xs font-semibold text-muted"
          >
            {lane.label}
            <span className="ml-auto rounded-full bg-surface px-1.5 text-[11px]">{items.length}</span>
          </div>
          <div className="flex max-h-[calc(100vh-260px)] flex-col gap-2 overflow-y-auto p-2.5">
            {items.map(({ request, specs }) => (
              <LaneCard
                key={request.id}
                request={request}
                specs={specs}
                platforms={platforms}
                selected={request.id === openedId}
                onOpen={onOpen}
                onAssigned={onAssigned}
              />
            ))}
          </div>
        </div>
      ))}
    </div>
  );
}

function LaneCard({
  request,
  specs,
  platforms,
  selected,
  onOpen,
  onAssigned,
}: {
  request: DevRequest;
  specs: DevRequestSpecLink[];
  platforms: string[];
  /** 위에 펼쳐 놓은 그 요청인지 — 어느 카드를 눌렀는지 보드에서도 보이게 한다. */
  selected: boolean;
  onOpen: (id: number) => void;
  onAssigned: () => void;
}): React.JSX.Element {
  return (
    <article
      title={request.title}
      onClick={() => onOpen(request.id)}
      className={`cursor-pointer rounded-md border bg-surface p-2.5 hover:border-accent ${
        selected ? 'border-accent' : 'border-line'
      }`}
    >
      <div className="line-clamp-3 text-[13px] leading-snug">{request.title}</div>
      {/* slug과 진행 상태를 한 줄에 이어 붙이면 긴 slug이 상태를 밀어내고 잘린다(컬럼 폭 260px).
          줄이는 것은 slug만, 상태는 항상 보이게 둔다 — 보드에서 알고 싶은 건 slug이 아니라 상태다. */}
      {specs.map((spec) => (
        <div key={`${spec.source}/${spec.slug}`} className="mt-1.5 flex items-center gap-1.5">
          <Link
            to={DevRequestFlow.specBoardPath(spec)}
            onClick={(event) => event.stopPropagation()}
            className="min-w-0 flex-1 truncate font-mono text-[11px] text-accent hover:underline"
            title={`${spec.slug} — ${spec.title}`}
          >
            {spec.slug}
          </Link>
          <Chip tone="repo">{DevRequestFlow.specProgressLabel(spec)}</Chip>
        </div>
      ))}
      {specs.length === 0 && <div className="mt-1.5 text-[11px] text-faint">연결된 스펙 없음</div>}
      <footer className="mt-2 flex flex-wrap items-center gap-1.5">
        <SourceChip source={request.source} />
        <PlatformQuickPick request={request} platforms={platforms} locked={specs.length > 0} onChanged={onAssigned} />
        <AssigneeQuickPick request={request} onAssigned={onAssigned} />
        {request.revision > 1 && <Chip tone="warn">rev {request.revision}</Chip>}
        {request.attachments.length > 0 && <Chip tone="repo">첨부 {request.attachments.length}</Chip>}
        <span className="flex-1" />
        <span className="text-[11px] text-faint">{localTime(request.requestedAt)}</span>
      </footer>
    </article>
  );
}

/**
 * 목록 모드의 한 줄.
 *
 * 본문은 드로어가 맡는다 — 줄 안에서 펼치면 같은 원문을 두 곳에서 그리게 되고, 보드 모드와 여는 방식도
 * 갈린다. 여기서는 한눈에 훑을 것(제목·출처·개정·첨부·연결 스펙 진행)만 세운다.
 */
function RequestRow({
  request,
  specs,
  platforms,
  selected,
  onOpen,
  onAssigned,
}: {
  request: DevRequest;
  specs: DevRequestSpecLink[];
  platforms: string[];
  selected: boolean;
  onOpen: (id: number) => void;
  onAssigned: () => void;
}): React.JSX.Element {
  return (
    <article
      onClick={() => onOpen(request.id)}
      className={`flex cursor-pointer flex-wrap items-baseline gap-2 rounded-lg border bg-surface px-4 py-3 hover:bg-canvas ${
        selected ? 'border-accent' : 'border-line'
      }`}
    >
      <DevRequestSummaryRow request={request} />
      <SourceChip source={request.source} />
      <PlatformQuickPick request={request} platforms={platforms} locked={specs.length > 0} onChanged={onAssigned} />
      <AssigneeQuickPick request={request} onAssigned={onAssigned} />
      {/* 개정된 요청만 표시한다 — 대부분은 rev 1이라 전부 붙이면 눈에 안 들어온다. */}
      {request.revision > 1 && <Chip tone="warn">rev {request.revision}</Chip>}
      {request.attachments.length > 0 && <Chip tone="repo">첨부 {request.attachments.length}</Chip>}
      {/* 스펙이 생겼는지가 이 인박스의 처리 상태다 — 아직 아무도 안 잡은 요청이 여기서 갈린다. */}
      {specs.length === 0 ? (
        <Chip>미착수</Chip>
      ) : (
        specs.map((spec) => (
          <Chip key={`${spec.source}/${spec.slug}`} tone="repo">
            {DevRequestFlow.specProgressLabel(spec)}
          </Chip>
        ))
      )}
      <span className="flex-1" />
      {/* 상세를 열지 않고도 원문으로 넘어갈 수 있게 줄에 둔다. 줄 클릭(상세 열기)과 섞이지 않게 전파를 끊는다. */}
      <SlackLink href={request.permalink} onClick={(event) => event.stopPropagation()} />
    </article>
  );
}

/**
 * 이 요청이 어떤 스펙이 됐고 어디까지 갔는지.
 *
 * 연결은 PRD의 `Dev request` 절에서 온다 — `nd-prd-intake`가 요청 문서의 번호를 옮겨 적고, 색인이
 * 그 값을 읽는다. 아직 없으면 착수 전이므로 어디서 시작하는지를 알려준다.
 *
 * 스펙 이름은 **에픽 보드로 가는 링크**다. "연결됨"만 적어 두면 그래서 그게 어떻게 됐는지를 보러
 * 갈 데가 없다.
 */
function LinkedSpecs({ specs, slug }: { specs: DevRequestSpecLink[]; slug: string }): React.JSX.Element {
  if (specs.length === 0) {
    return (
      <p className="mb-3 rounded-md border border-line-soft bg-canvas px-3 py-2 text-[11.5px] text-muted">
        아직 연결된 스펙이 없다. 하네스의 <code className="font-mono">dev-requests/</code> 문서로{' '}
        <code className="font-mono">nd-prd-intake</code>를 돌리면 <code className="font-mono">specs/{slug}</code>가
        만들어지고 여기 뜬다.
      </p>
    );
  }

  return (
    <ul className="mb-3 flex flex-col gap-1.5 rounded-md border border-line-soft bg-canvas px-3 py-2">
      {specs.map((spec) => (
        <li key={`${spec.source}/${spec.slug}`} className="flex flex-wrap items-baseline gap-1.5 text-[11.5px]">
          <Chip>{spec.source}</Chip>
          <Link to={DevRequestFlow.specBoardPath(spec)} className="font-mono text-accent hover:underline">
            {spec.slug} ↗
          </Link>
          <span className="text-faint">· {spec.title}</span>
          <span className="flex-1" />
          <Chip tone="repo">{DevRequestFlow.specProgressLabel(spec)}</Chip>
          <span className="font-mono text-[11px] text-faint" title={spec.path}>
            {spec.updatedAt ? `갱신 ${spec.updatedAt}` : ''}
          </span>
        </li>
      ))}
    </ul>
  );
}

/**
 * 개정 이력 — 펼칠 때 받는다. 목록 응답에 싣지 않는 이유는 본문이 커서, 개정이 없는 대부분의 요청까지
 * 무거워지기 때문이다.
 */
function RevisionHistory({ requestId, current }: { requestId: number; current: number }): React.JSX.Element {
  const [revisions, setRevisions] = useState<DevRequestRevision[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [shown, setShown] = useState<number | null>(null);

  useEffect(() => {
    devRequestApi
      .revisions(requestId)
      .then((payload) => setRevisions(payload.revisions))
      .catch((cause: Error) => setError(cause.message));
  }, [requestId]);

  return (
    <section className="mt-4 rounded-md border border-line-soft bg-canvas p-3">
      <h3 className="text-[12px] font-semibold">
        개정 이력
        <span className="ml-1.5 font-normal text-faint">
          현재 rev {current} · 아래는 밀려난 이전 본문
        </span>
      </h3>

      {error && <div className="mt-2 text-[11.5px] text-rose-700">이력을 불러오지 못했다 — {error}</div>}
      {!error && !revisions && <div className="mt-2 text-[11.5px] text-muted">불러오는 중…</div>}

      {revisions?.map((revision) => (
        <div key={revision.revision} className="mt-2 border-t border-line-soft pt-2 first:border-0 first:pt-0">
          <button
            type="button"
            onClick={() => setShown((value) => (value === revision.revision ? null : revision.revision))}
            className="flex w-full cursor-pointer flex-wrap items-baseline gap-2 text-left"
          >
            <span className="font-mono text-[11.5px] text-muted">rev {revision.revision}</span>
            <span className="text-[12px]">{revision.title}</span>
            <span className="flex-1" />
            <span className="text-[11px] text-faint">
              {localTime(revision.requestedAt)} 요청 · {localTime(revision.archivedAt)} 교체
            </span>
          </button>
          {shown === revision.revision && (
            <div className="mt-2 rounded-md border border-line bg-surface p-3">
              <div className="mb-2">
                <SlackLink href={revision.permalink} label="이 버전을 보낸 메시지" />
              </div>
              <AttachmentLinks attachments={revision.attachments} />
              <Markdown text={revision.body} />
            </div>
          )}
        </div>
      ))}
    </section>
  );
}

/** 슬랙 원문으로 나가는 링크. 요청 스레드가 그 요청의 대화 이력이라 자주 열게 된다. */
function SlackLink({
  href,
  label,
  onClick,
}: {
  href: string | null;
  label?: string;
  onClick?: (event: React.MouseEvent) => void;
}): React.JSX.Element | null {
  if (href === null) return null;
  return (
    <a
      href={href}
      target="_blank"
      rel="noreferrer"
      onClick={onClick}
      className="text-[11px] text-accent hover:underline"
    >
      {label ?? '슬랙 원문'} ↗
    </a>
  );
}

function AttachmentLinks({ attachments }: { attachments: DevRequestAttachment[] }): React.JSX.Element | null {
  if (attachments.length === 0) return null;
  return (
    <div className="mb-3 flex flex-wrap gap-2">
      {attachments.map((file) => (
        <a
          key={file.permalink}
          href={file.permalink}
          target="_blank"
          rel="noreferrer"
          className="text-[11.5px] text-accent hover:underline"
        >
          📎 {file.name}
        </a>
      ))}
    </div>
  );
}

/**
 * 주소창 `item` 쿼리(`#42`)에서 요청 id를 꺼낸다. `DrawerPayloads.devRequest`의 `key`(`#${request.id}`)와
 * 같은 모양이어야 왕복이 맞는다 — 다른 화면의 `item` 값도 그 항목의 `key`를 그대로 쓴다.
 */
function idOfItemParam(item: string | null): number | null {
  if (!item) return null;
  const id = Number(item.replace(/^#/, ''));
  return Number.isInteger(id) ? id : null;
}

/** 출처 칩 — 콘솔 역등록은 슬랙 출처와 구분되게 사람 말로 보인다. 보드 카드와 목록 줄이 같은 칩을 쓴다. */
function SourceChip({ source }: { source: string }): React.JSX.Element {
  const fromConsole = source === 'console';
  return <span title={fromConsole ? '이미 진행된 작업을 묶은 요청서' : undefined}><Chip>{fromConsole ? '콘솔 역등록' : source}</Chip></span>;
}

/** 저장은 UTC ISO다 — 읽는 사람은 한국 시간으로 본다. */
function localTime(iso: string): string {
  return new Date(iso).toLocaleString('ko-KR', { dateStyle: 'short', timeStyle: 'short' });
}
